import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Phone, ShieldCheck, Wrench, Truck, HardHat, Award, Users } from "lucide-react";
import SEO from "../components/SEO";
import SectionHeader from "../components/SectionHeader";
import { useSiteData, useLocaleText } from "../contexts/SiteDataContext";

const Home = () => {
  const { t } = useTranslation();
  const { siteContent, services, gallery, clients } = useSiteData();
  const { pick } = useLocaleText();

  const phones = siteContent?.phones || ["+974 50871458"];
  const stats = siteContent?.stats || [];
  const values = siteContent?.values || [];
  const rental = (services || []).filter((s) => s.type === "rental").slice(0, 8);
  const other = (services || []).filter((s) => s.type === "other").slice(0, 4);
  const projectPhotos = (gallery || []).filter((g) => g.cat === "projects").slice(0, 6);
  const activeClients = (clients || []).filter((c) => c.is_active);

  return (
    <div data-testid="page-home">
      <SEO title={t("nav.home")} description={pick(siteContent?.hero_subtitle)} path="/" />

      <section className="relative bg-[#111111] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/hero-bg.jpg"
            alt="Excavator on construction site"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/90 via-[#111111]/55 to-[#111111]/20 rtl:bg-gradient-to-l" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-12 md:py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5 rise">
              <span className="w-12 h-[2px] bg-[#FF5A00]" />
              <span className="font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#FF5A00]">
                {t("home.eyebrow")}
              </span>
            </div>
            <h1 className="font-display uppercase font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] rise delay-1" data-testid="hero-title">
              {pick(siteContent?.hero_title) || t("home.title1") + " " + t("home.title2")}
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/80 max-w-lg leading-relaxed rise delay-2">
              {pick(siteContent?.hero_subtitle) || pick(siteContent?.tagline)}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 rise delay-3">
              <Link to="/services" className="inline-flex items-center justify-center gap-2 bg-[#FF5A00] text-white px-7 py-3.5 font-bold uppercase tracking-wide hover:bg-white hover:text-[#111111] transition-colors duration-300" data-testid="hero-cta-services">
                {t("home.ctaServices")} <ArrowRight size={16} className="rtl:rotate-180" />
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-7 py-3.5 font-bold uppercase tracking-wide hover:bg-white hover:text-[#111111] transition-colors" data-testid="hero-cta-contact">
                {t("home.ctaContact")}
              </Link>
            </div>
          </div>
        </div>

        <div className="relative bg-[#FF5A00] text-[#111111] border-t-4 border-[#111111] overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap py-3 md:py-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex shrink-0">
                {[
                  t("home.marqueeExcavation"),
                  t("home.marqueeDemolition"),
                  t("home.marqueeInterlock"),
                  t("home.marqueeRental"),
                  t("home.marqueeRoadWorks"),
                  t("home.marqueeDoha"),
                ].map((label) => (
                  <span key={`${i}-${label}`} className="font-display text-xl sm:text-2xl md:text-3xl uppercase font-bold mx-5 md:mx-8 flex items-center gap-4 md:gap-8">
                    {label}
                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[#111111] rotate-45" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {stats.length > 0 && (
        <section className="py-8 md:py-10 bg-white border-b border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5E7EB]">
            {stats.map((s, i) => (
              <div key={i} className="bg-white p-5 sm:p-6 md:p-8 lg:p-10 hover:bg-[#111111] hover:text-white transition-colors group cursor-default" data-testid={`stat-${i}`}>
                <div className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#FF5A00]">{s.value}</div>
                <div className="mt-2 font-mono-rtc text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#52525B] group-hover:text-white/70">
                  {pick({ en: s.label_en, ar: s.label_ar })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="py-10 md:py-14 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
            <SectionHeader eyebrow={t("home.servicesEyebrow")} title={t("home.servicesTitle")} subtitle={t("home.servicesSubtitle")} invert />
            <Link to="/services" className="inline-flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-white/70 hover:text-[#FF5A00] transition-colors shrink-0" data-testid="services-view-all">
              {t("common.viewAll")} <ArrowRight size={16} className="rtl:rotate-180" />
            </Link>
          </div>

          <div className="flex flex-col gap-5">
            {/* Featured Rental — TOP, full width, shows full equipment */}
            <Link to="/services/rental" className="group relative overflow-hidden bg-[#0d0d0d] text-white border-2 border-[#333] hover:border-[#FF5A00] block-shadow-orange min-h-[260px] md:min-h-[320px] flex" data-testid="bento-rental">
              {/* Full equipment image — flips side via RTL */}
              <img
                src="/heavy-equipment-rental.jpg"
                alt={t("home.heavyEquipment")}
                className="absolute inset-0 w-full h-full object-contain object-right rtl:object-left group-hover:scale-105 transition-transform duration-700"
              />
              {/* Overlay — gradient direction flips in RTL automatically */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/98 via-[#0d0d0d]/70 to-transparent rtl:bg-gradient-to-l" />
              {/* Content — sits left in LTR, right in RTL */}
              <div className="relative z-10 p-8 md:p-10 flex flex-col justify-between w-full md:max-w-[55%] rtl:ml-auto">
                <div>
                  <span className="inline-flex items-center gap-2 bg-[#FF5A00] text-white text-[10px] font-mono-rtc uppercase tracking-[0.3em] px-3 py-1.5">
                    {t("home.featured")} · {t("home.rentalLabel")}
                  </span>
                </div>
                <div>
                  <h3 className="font-display uppercase font-bold text-2xl sm:text-3xl md:text-4xl leading-tight">
                    {t("home.heavyEquipment")}<br /><span className="text-[#FF5A00]">{t("nav.rental")}</span>
                  </h3>
                  <p className="mt-3 text-white/80 text-sm md:text-base max-w-md">{t("home.rentalDesc")}</p>
                  <div className="mt-5 inline-flex items-center gap-2 bg-white text-[#111111] px-5 py-2.5 font-bold uppercase tracking-wide text-sm group-hover:bg-[#FF5A00] group-hover:text-white transition-colors duration-300">
                    {t("home.browseFleet")} <ArrowRight size={14} className="rtl:rotate-180" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Other services — BOTTOM row with background images */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              {other.map((s, i) => (
                <Link to={`/services/${s.slug}`} key={s.slug} className="group relative overflow-hidden border-2 border-[#333] hover:border-[#FF5A00] block-shadow-to-orange min-h-[210px] text-white" data-testid={`bento-service-${s.slug}`}>
                  {/* Service-specific background image */}
                  <img
                    src={s.image}
                    alt={pick(s.name)}
                    className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/95 via-[#111111]/50 to-[#111111]/20" />
                  <div className="relative z-10 p-6 h-full flex flex-col justify-between">
                    <div>
                      <span className="font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-white/60">0{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-display uppercase font-bold text-3xl text-white group-hover:text-[#FF5A00] transition-colors duration-300">{pick(s.name)}</h3>
                      <p className="mt-1.5 text-sm text-white/75">{pick(s.short)}</p>
                      <div className="mt-3 inline-flex items-center gap-2 font-mono-rtc text-[11px] uppercase tracking-[0.2em] text-white/70 group-hover:text-[#FF5A00] transition-colors duration-300">
                        {t("common.viewDetails")} <ArrowRight size={12} className="rtl:rotate-180" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {values.length > 0 && (
        <section className="py-10 md:py-14 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-5">
                <SectionHeader eyebrow={t("home.whyEyebrow")} title={t("home.whyTitle")} subtitle={t("home.whySubtitle")} />
                <Link to="/about" className="mt-8 inline-flex items-center gap-2 bg-[#FF5A00] text-white px-7 py-3.5 font-bold uppercase tracking-wide hover:bg-[#111111] transition-colors" data-testid="why-us-cta">
                  {t("common.learnMoreAbout")} <ArrowRight size={16} className="rtl:rotate-180" />
                </Link>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {values.map((v, i) => {
                  const Icon = [ShieldCheck, HardHat, Award, Users][i % 4];
                  return (
                    <div key={i} className="bg-white border-2 border-[#E5E7EB] p-7 hover:border-[#FF5A00] block-shadow-to-orange transition-all duration-300 group cursor-default" data-testid={`value-${i}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="font-mono-rtc text-xs text-[#FF5A00]">0{i + 1}</span>
                        <Icon size={18} className="text-[#FF5A00] group-hover:scale-110 transition-transform duration-300" />
                      </div>
                      <h4 className="font-display text-2xl uppercase font-bold text-[#111111] group-hover:text-[#FF5A00] transition-colors duration-300">{pick({ en: v.title_en, ar: v.title_ar })}</h4>
                      <p className="mt-2 text-[#52525B] text-sm leading-relaxed">{pick({ en: v.text_en, ar: v.text_ar })}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {rental.length > 0 && (
        <section className="py-10 md:py-14 bg-[#FAFAFA] relative overflow-hidden">
          {/* Subtle orange accent strip on left */}
          <div className="absolute top-0 left-0 w-1 h-full bg-[#FF5A00] hidden lg:block" />
          <div className="max-w-7xl mx-auto px-6 relative">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
              <SectionHeader eyebrow={t("home.fleetEyebrow")} title={t("home.fleetTitle")} subtitle={t("home.fleetSubtitle")} />
              <Link to="/services/rental" className="inline-flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-[#111111] hover:text-[#FF5A00] transition-colors shrink-0 link-underline" data-testid="view-rental-cta">
                {t("common.viewFullCatalog")} <ArrowRight size={14} className="rtl:rotate-180" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {rental.map((eq, i) => (
                <Link
                  to={`/services/${eq.slug}`}
                  key={eq.slug}
                  className="group relative overflow-hidden bg-white border border-[#E5E7EB] hover:border-[#FF5A00] transition-all duration-300 hover:shadow-[6px_6px_0_#FF5A00] hover:-translate-y-0.5 flex flex-col"
                  data-testid={`fleet-item-${eq.slug}`}
                >
                  {/* Image area — image fills the box */}
                  <div className="relative aspect-[4/3] bg-[#F3F4F6] overflow-hidden">
                    {/* Capacity badge */}
                    {eq.capacity && (
                      <div className="absolute top-2 right-2 z-10">
                        <span className="inline-block bg-[#FF5A00] text-white font-mono-rtc text-[9px] uppercase tracking-widest font-bold px-2 py-0.5">
                          {eq.capacity}
                        </span>
                      </div>
                    )}
                    {/* Equipment image — covers box completely */}
                    <img
                      src={eq.image}
                      alt={pick(eq.name)}
                      className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  {/* Caption bar */}
                  <div className="p-3 md:p-4 border-t border-[#E5E7EB] bg-white flex items-center justify-between gap-2">
                    <h4 className="font-display uppercase font-bold text-[#111111] text-sm md:text-base leading-tight truncate group-hover:text-[#FF5A00] transition-colors duration-300">
                      {pick(eq.name)}
                    </h4>
                    <ArrowRight size={14} className="text-[#FF5A00] shrink-0 group-hover:translate-x-1 transition-transform duration-300 rtl:rotate-180" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Projects Section ── */}
      {projectPhotos.length > 0 && (
        <section className="py-10 md:py-14 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
              <SectionHeader eyebrow={t("home.projectsEyebrow")} title={t("home.projectsTitle")} subtitle={t("home.projectsSubtitle")} />
              <Link to="/gallery" className="inline-flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-[#111111] hover:text-[#FF5A00] transition-colors link-underline shrink-0" data-testid="projects-view-all">
                {t("common.viewAll")} <ArrowRight size={16} className="rtl:rotate-180" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {projectPhotos.map((img, i) => (
                <div key={img.id || i} className="group relative aspect-[4/3] overflow-hidden border-2 border-[#111111] hover:border-[#FF5A00] block-shadow-to-orange bg-[#F3F4F6]" data-testid={`project-photo-${i}`}>
                  <img src={img.src} alt={pick(img.alt)} loading="lazy" className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Clients Section — Horizontal marquee ── */}
      {activeClients.length > 0 && (
        <section className="py-10 md:py-12 bg-white border-t border-[#E5E7EB] overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 mb-8">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-3">
                <span className="w-8 h-[2px] bg-[#FF5A00]" />
                <span className="font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#FF5A00]">{t("home.clientsEyebrow")}</span>
                <span className="w-8 h-[2px] bg-[#FF5A00]" />
              </div>
              <h2 className="font-display uppercase font-bold text-2xl sm:text-3xl md:text-4xl text-[#111111]">{t("home.clientsTitle")}</h2>
              <p className="mt-3 text-sm md:text-base text-[#52525B] max-w-xl mx-auto">{t("home.clientsSubtitle")}</p>
            </div>
          </div>

          {/* Continuous horizontal marquee — pauses on hover */}
          <div className="relative group">
            {/* Edge fades */}
            <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

            <div className="flex animate-marquee-slow whitespace-nowrap group-hover:[animation-play-state:paused]">
              {Array.from({ length: 2 }).map((_, dup) => (
                <div key={dup} className="flex shrink-0 gap-3 md:gap-4 px-2" aria-hidden={dup === 1}>
                  {activeClients.map((client) => (
                    <div
                      key={`${dup}-${client.id}`}
                      className="group/c shrink-0 w-[180px] md:w-[220px] h-20 md:h-24 flex items-center justify-center border border-[#E5E7EB] bg-white px-5 transition-all duration-300 hover:border-[#FF5A00] hover:shadow-[4px_4px_0_#FF5A00]"
                      data-testid={dup === 0 ? `client-${client.id}` : undefined}
                    >
                      {client.logo_url ? (
                        <img
                          src={client.logo_url}
                          alt={client.name}
                          className="max-h-14 max-w-full object-contain filter grayscale opacity-70 group-hover/c:grayscale-0 group-hover/c:opacity-100 transition-all duration-300"
                        />
                      ) : (
                        <span className="font-display text-xs md:text-sm uppercase tracking-wide text-center text-[#52525B] group-hover/c:text-[#FF5A00] transition-colors leading-tight whitespace-normal">
                          {client.name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-8 md:py-10 bg-[#FF5A00] text-[#111111] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6 md:gap-10 items-center">
          <h2 className="font-display uppercase font-bold text-2xl sm:text-3xl md:text-4xl leading-tight">{t("home.ctaBannerTitle")}</h2>
          <div>
            <p className="text-base md:text-lg text-[#111111]/80 max-w-md">{t("home.ctaBannerText")}</p>
            <div className="mt-5 md:mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#111111] text-white px-7 py-3.5 font-bold uppercase tracking-wide hover:bg-white hover:text-[#111111] transition-colors" data-testid="cta-banner-contact">
                {t("common.startProject")} <ArrowRight size={16} className="rtl:rotate-180" />
              </Link>
              <a href={`tel:${phones[0].replace(/\s/g, "")}`} className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#111111] px-7 py-3.5 font-bold uppercase tracking-wide hover:bg-[#111111] hover:text-white transition-colors" data-testid="cta-banner-call">
                <Phone size={16} /> <span dir="ltr">{phones[0]}</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
